import { verifyOtp } from './api'
import { widgetStyles } from './styles'
import { t } from './translations'
import type { Events, Providers, SessionCreateResult } from './types'

const OTP_LENGTH = 4

/** How long the "Welcome back" loading screen shows before the OTP form. */
const WELCOME_DELAY_MS = 1200

export interface MountParams {
  /** CSS selector of the container the app provides (e.g. '.ownid-container'). */
  container: string
  loginId: string
  apiUrl: string
  providers?: Providers
  events?: Events
}

/** White person glyph shown in the blue avatar pill (OwnID's exact icon). */
const PERSON_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 13" aria-hidden="true" focusable="false">' +
  '<path d="M3 3.334c0 1.654 1.346 3 3 3s3-1.346 3-3-1.346-3-3-3-3 1.346-3 3zM11.333 13H12v-.667c0-2.573-2.094-4.667-4.667-4.667H4.667C2.093 7.667 0 9.761 0 12.334V13h11.333z"/>' +
  '</svg>'

/** OwnID's two-circle loading spinner (static track + rotating dashed arc). */
const SPINNER =
  '<div class="ownid-widget__spinner">' +
  '<svg viewBox="0 0 100 100" aria-hidden="true"><circle class="ownid-widget__spinner-bg" r="42.5" cx="50" cy="50"></circle></svg>' +
  '<svg class="ownid-widget__spinner-arc-svg" viewBox="0 0 100 100" aria-hidden="true"><circle class="ownid-widget__spinner-arc" r="42.5" cx="50" cy="50"></circle></svg>' +
  '</div>'

/**
 * Render the mock OTP widget into the container. Mirrors the real SDK: mounts a
 * single <ownid-root> custom element with an open shadow root whose first child
 * is the <style> (the app appends override rules to that node).
 */
export function mountWidget(params: MountParams): void {
  const host = document.querySelector(params.container)
  if (!host) {
    console.error(
      `[ownid-mock] container "${params.container}" not found in the DOM`
    )
    return
  }

  // Clear any previous render (e.g. a retried start()).
  host.querySelectorAll('ownid-root').forEach((el) => el.remove())

  const root = document.createElement('ownid-root')
  // The real OwnID SDK ships its <ownid-root> host hidden and reveals it once
  // the widget paints; environments that host the widget rely on that reveal,
  // leaving the host at `visibility: hidden` otherwise. Make our host visible
  // explicitly (inline + !important so it wins over any such gating rule).
  root.style.setProperty('visibility', 'visible', 'important')
  root.style.setProperty('display', 'block', 'important')
  const shadow = root.attachShadow({ mode: 'open' })

  const style = document.createElement('style')
  style.textContent = widgetStyles
  shadow.appendChild(style) // MUST be children[0] — see styles.ts note.

  const widget = document.createElement('div')
  widget.className = 'ownid-widget'
  widget.setAttribute('data-ownid-content', '')
  shadow.appendChild(widget)
  host.appendChild(root)

  new OtpWidget(widget, root, params).start()
}

class OtpWidget {
  private inputs: HTMLInputElement[] = []
  private errorEl!: HTMLDivElement
  private resendBtn!: HTMLButtonElement
  private loaderRow!: HTMLDivElement
  private resendRow!: HTMLDivElement
  private submitting = false
  private loadingTimer: ReturnType<typeof setTimeout> | null = null

  constructor(
    private readonly el: HTMLElement,
    private readonly root: HTMLElement,
    private readonly params: MountParams
  ) {}

  /** Show the "Welcome back" loader, then swap to the OTP form (like OwnID). */
  start(): void {
    this.renderLoading()
    this.loadingTimer = setTimeout(() => this.renderForm(), WELCOME_DELAY_MS)
  }

  private renderLoading(): void {
    this.el.replaceChildren()
    const title = document.createElement('h2')
    title.className = 'ownid-widget__title'
    title.setAttribute('data-testid', 'loading--title')
    title.textContent = t('otp.welcome')

    const row = document.createElement('div')
    row.className = 'ownid-widget__loader-row'
    row.innerHTML = SPINNER

    this.el.append(title, row)
  }

  private renderForm(): void {
    this.el.replaceChildren()
    this.inputs = []
    // Title
    const title = document.createElement('h2')
    title.className = 'ownid-widget__title'
    title.setAttribute('data-testid', 'otp--title')
    title.textContent = t('otp.title')

    // Login-id row: pill with blue person avatar + phone number.
    const loginRow = document.createElement('div')
    loginRow.className = 'ownid-widget__login-row'
    const pill = document.createElement('div')
    pill.className = 'ownid-widget__pill'
    const avatar = document.createElement('span')
    avatar.className = 'ownid-widget__avatar'
    avatar.innerHTML = PERSON_ICON
    const phone = document.createElement('span')
    phone.className = 'ownid-widget__phone'
    phone.title = this.params.loginId
    phone.textContent = this.params.loginId
    pill.append(avatar, phone)
    loginRow.appendChild(pill)

    // Description
    const description = document.createElement('div')
    description.className = 'ownid-widget__description'
    description.setAttribute('data-testid', 'otp--description')
    description.textContent = t('otp.description')

    // OTP boxes
    const otp = document.createElement('div')
    otp.className = 'ownid-widget__otp'
    const form = document.createElement('div')
    form.className = 'otp-input-module__form'
    for (let i = 0; i < OTP_LENGTH; i++) {
      const box = document.createElement('span')
      box.className = 'otp-input-module__input'
      const input = this.buildDigitInput(i)
      box.appendChild(input)
      form.appendChild(box)
      this.inputs.push(input)
    }
    otp.appendChild(form)

    // Verifying loader: the same spinner, shown below the OTP field on submit.
    this.loaderRow = document.createElement('div')
    this.loaderRow.className = 'ownid-widget__loader-row'
    this.loaderRow.style.display = 'none'
    this.loaderRow.innerHTML = SPINNER

    this.errorEl = document.createElement('div')
    this.errorEl.className = 'ownid-widget__error'
    this.errorEl.setAttribute('role', 'alert')

    // Resend (text button)
    this.resendRow = document.createElement('div')
    this.resendRow.className = 'ownid-widget__resend-row'
    this.resendBtn = document.createElement('button')
    this.resendBtn.className = 'ownid-widget__resend'
    this.resendBtn.type = 'button'
    this.resendBtn.setAttribute('data-testid', 'otp--resend')
    this.resendBtn.textContent = t('otp.resend')
    // No real SMS in the mock — resend just clears for another try.
    this.resendBtn.addEventListener('click', () => this.reset(true))
    this.resendRow.appendChild(this.resendBtn)

    this.el.append(
      title,
      loginRow,
      description,
      otp,
      this.loaderRow,
      this.errorEl,
      this.resendRow
    )

    this.inputs[0]?.focus()
  }

  private buildDigitInput(index: number): HTMLInputElement {
    const input = document.createElement('input')
    input.type = 'text'
    input.inputMode = 'numeric'
    input.autocomplete = 'one-time-code'
    input.maxLength = 1
    input.setAttribute('data-testid', `ownid-otp-input-${index}`)
    input.setAttribute('aria-label', `OTP digit ${index + 1} of ${OTP_LENGTH}`)

    input.addEventListener('input', () => {
      input.value = input.value.replace(/\D/g, '').slice(0, 1)
      if (input.value && index < OTP_LENGTH - 1) {
        this.inputs[index + 1].focus()
      }
      this.onChange()
    })

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Backspace' && !input.value && index > 0) {
        this.inputs[index - 1].focus()
      }
    })

    input.addEventListener('paste', (e) => {
      e.preventDefault()
      const text = (e.clipboardData?.getData('text') ?? '')
        .replace(/\D/g, '')
        .slice(0, OTP_LENGTH)
      if (!text) return
      ;[...text].forEach((char, i) => {
        if (this.inputs[i]) this.inputs[i].value = char
      })
      this.inputs[Math.min(text.length, OTP_LENGTH - 1)].focus()
      this.onChange()
    })

    return input
  }

  private get code(): string {
    return this.inputs.map((i) => i.value).join('')
  }

  private onChange(): void {
    this.clearError()
    // Match OwnID UX: auto-submit once all digits are present (no CTA button).
    if (this.code.length === OTP_LENGTH && !this.submitting) this.submit()
  }

  private async submit(): Promise<void> {
    if (this.submitting || this.code.length !== OTP_LENGTH) return
    this.setSubmitting(true)

    const result = await verifyOtp({
      apiUrl: this.params.apiUrl,
      code: this.code,
      phoneNumber: this.params.loginId,
    })

    if (!result.ok) {
      if (result.code === 'ACCOUNT_NOT_FOUND') {
        await safe(this.params.events?.onAccountNotFound)
        this.teardown()
        return
      }
      const message = errorMessageForCode(result.code, result.message)
      this.showError(message)
      await safe(() =>
        this.params.events?.onError?.({ code: result.code, message })
      )
      this.setSubmitting(false)
      this.reset(true)
      return
    }

    // Success: hand the JWT to the app exactly like the real SDK does.
    const sessionResult = await this.createSession(result.jwt)
    if (sessionResult.status !== 'logged-in') {
      this.showError(errorMessageForCode('SERVER_ERROR'))
      this.setSubmitting(false)
      this.reset(true)
      return
    }

    await safe(this.params.events?.onFinish)
    this.teardown()
  }

  private async createSession(jwt: string): Promise<SessionCreateResult> {
    const create = this.params.providers?.session?.create
    if (!create) return { status: 'logged-in' } // nothing to persist into
    try {
      return await create({ jwt })
    } catch {
      return { status: 'fail', reason: 'session create threw' }
    }
  }

  private setSubmitting(value: boolean): void {
    this.submitting = value
    this.inputs.forEach((i) => (i.disabled = value))
    // Swap the resend link for the spinner (below the OTP field) while verifying.
    this.loaderRow.style.display = value ? 'flex' : 'none'
    this.resendRow.style.display = value ? 'none' : 'flex'
  }

  private reset(focus: boolean): void {
    this.inputs.forEach((i) => {
      i.value = ''
      i.disabled = false
    })
    if (focus) this.inputs[0]?.focus()
  }

  private showError(message: string): void {
    this.errorEl.textContent = message
    this.el.classList.add('is-error')
  }

  private clearError(): void {
    if (!this.errorEl.textContent) return
    this.errorEl.textContent = ''
    this.el.classList.remove('is-error')
  }

  private teardown(): void {
    if (this.loadingTimer) clearTimeout(this.loadingTimer)
    this.root.remove()
  }
}

/** Inline error copy: code-specific translation → server message → generic default. */
function errorMessageForCode(code: string, serverMessage?: string): string {
  const key = `otp.errors.${code}`
  const translated = t(key)
  if (translated !== key) return translated
  if (serverMessage) return serverMessage
  return t('otp.errors.default')
}

/** Run an optional callback, swallowing errors so one bad handler can't break the flow. */
async function safe(fn?: () => void | Promise<void>): Promise<void> {
  if (!fn) return
  try {
    await fn()
  } catch (err) {
    console.error('[ownid-mock] event handler threw', err)
  }
}
